import classNames from "classnames";
import { MouseEventHandler, ReactNode } from "react";
import { ClipLoader } from "react-spinners";

import { CheckIcon } from "../../icons/CheckIcon/CheckIcon";
import { ClockIcon } from "../../icons/ClockIcon/ClockIcon";
import { CommentIcon } from "../../icons/CommentIcon/CommentIcon";
import { DefectIcon } from "../../icons/DefectIcon/DefectIcon";
import { DeleteIcon } from "../../icons/DeleteIcon/DeleteIcon";
import { DetachIcon } from "../../icons/DetachIcon/DetachIcon";
import { DocumentIcon } from "../../icons/DocumentIcon/DocumentIcon";
import { FilterIcon } from "../../icons/FilterIcon/FilterIcon";
import { LayersIcon } from "../../icons/LayersIcon/LayersIcon";
import { LeftArrowIcon } from "../../icons/LeftArrowIcon/LeftArrowIcon";
import { MonitorIcon } from "../../icons/MonitorIcon/MonitorIcon";
import { PlusIcon } from "../../icons/PlusIcon/PlusIcon";
import { ShareIcon } from "../../icons/ShareIcon/ShareIcon";
import { SummaryIcon } from "../../icons/SummaryIcon/SummaryIcon";
import { TableIcon } from "../../icons/TableIcon/TableIcon";
import { WrenchIcon } from "../../icons/WrenchIcon/WrenchIcon";

import styles from "./custom-button.module.css";

export type ButtonWidth =
  | 10
  | 20
  | 30
  | 40
  | 50
  | 60
  | 70
  | 80
  | 90
  | 100
  | 48
  | 36
  | 24
  | 16;
type ButtonColor =
  | "gray"
  | "green"
  | "white"
  | "red"
  | "lightGray"
  | "lightGrayNew"
  | "orange"
  | "lightGreen"
  | "transparent";
type ButtonIcon =
  | "monitor"
  | "wrench"
  | "layers"
  | "clock"
  | "check"
  | "leftarrow"
  | "plus"
  | "document"
  | "defect"
  | "delete"
  | "filterDark"
  | "filterLight"
  | "share"
  | "detach"
  | "commentDark"
  | "commentLight"
  | "commentRed"
  | "commentRedGreen"
  | "summary"
  | "table"
  | "summaryDark"
  | "tableDark";

type customButtonProps = {
  children?: ReactNode;
  color: ButtonColor;
  icon?: ButtonIcon;
  iconPosition?: "left" | "right";
  disabled?: boolean;
  width?: ButtonWidth;
  loading?: boolean;
  customClass?: any;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  iconSize?: number;
};

const COLORS: Record<string, string> = {
  gray: styles["custom-button__gray"],
  lightGray: styles["custom-button__light-gray"],
  green: styles["custom-button__green"],
  white: styles["custom-button__white"],
  red: styles["custom-button__red"],
  lightGrayNew: styles["custom-button__light-gray-new"],
  orange: styles["custom-button__orange"],
  lightGreen: styles["custom-button_light-green"],
  transparent: styles["custom-button__transparent"],
};

const ICONS: Record<string, JSX.Element> = {
  monitor: <MonitorIcon />,
  layers: <LayersIcon />,
  wrench: <WrenchIcon />,
  clock: <ClockIcon />,
  check: <CheckIcon color="dark" />,
  leftarrow: <LeftArrowIcon color="light" />,
  plus: <PlusIcon color="light" />,
  document: <DocumentIcon />,
  defect: <DefectIcon color="light" />,
  delete: <DeleteIcon />,
  filterDark: <FilterIcon color="dark" />,
  filterLight: <FilterIcon color="light" />,
  share: <ShareIcon />,
  detach: <DetachIcon />,
  commentDark: <CommentIcon color="dark" />,
  commentLight: <CommentIcon color="light" />,
  commentRed: <CommentIcon color="red" />,
  commentRedGreen: <CommentIcon color="redgreen" />,
  summary: <SummaryIcon />,
  table: <TableIcon />,
  summaryDark: <SummaryIcon color="dark" />,
  tableDark: <TableIcon color="dark" />,
};

export const CustomButton = ({
  children,
  color,
  icon,
  iconPosition = "right",
  disabled,
  width = 50,
  loading = false,
  customClass,
  onClick,
  iconSize,
}: customButtonProps): JSX.Element => {
  const buttonClassName = () =>
    classNames(styles["custom-button"], COLORS[color], customClass, {
      [styles["custom-button__left-icon"]]: iconPosition === "left",
      [styles["custom-button--disabled"]]: disabled || loading,
      [styles[`w-${width}`]]: width,
    });

  return (
    <button
      className={buttonClassName()}
      disabled={loading || disabled}
      onClick={onClick}
    >
      <ClipLoader color="#fff" loading={loading} size={40} />

      {!loading && (
        <>
          {icon && iconPosition === "left" && (
            <div
              className={styles["custom-button__icon"]}
              style={
                iconSize ? { width: iconSize, height: iconSize } : undefined
              }
            >
              {ICONS[icon]}
            </div>
          )}

          <span className="custom-button__text">{children}</span>

          {icon && iconPosition === "right" && (
            <div
              className={styles["custom-button__icon"]}
              style={
                iconSize ? { width: iconSize, height: iconSize } : undefined
              }
            >
              {ICONS[icon]}
            </div>
          )}
        </>
      )}
    </button>
  );
};
